import videojs from 'video.js'
import Player from 'video.js/dist/types/player'
import styles from '../VideoPlayer.module.less'

export class SegmentRangeControl extends videojs.getComponent('Component') {
    private draggingHandle: 'start' | 'end' | null = null
    private startHandleEl: HTMLElement | null = null
    private endHandleEl: HTMLElement | null = null
    private startHandleContainer: HTMLElement | null = null
    private endHandleContainer: HTMLElement | null = null
    private trackEl: HTMLElement | null = null
    private activeRangeEl: HTMLElement | null = null
    private playheadEl: HTMLElement | null = null
    private boundMouseMove: ((e: MouseEvent | TouchEvent) => void) | null = null
    private boundMouseUp: (() => void) | null = null
    private listenersSetup: boolean = false
    private _initializingSegment: boolean = false
    private _segmentInitialized: boolean = false
    private startDragStartHandler: ((e: Event) => void) | null = null
    private startDragEndHandler: ((e: Event) => void) | null = null
    private trackClickHandler: ((e: MouseEvent | TouchEvent) => void) | null = null
    private mousedownHandler: ((e: Event) => void) | null = null
    private touchstartHandler: ((e: Event) => void) | null = null

    constructor(player: Player, options: object) {
        super(player, options)
        this.boundMouseMove = this.handleMouseMove.bind(this)
        this.boundMouseUp = this.handleMouseUp.bind(this)
        this.player().on('timeupdate', () => this.update())
        this.player().on('segmentchange', () => this.update())
        this.player().on('loadedmetadata', () => this.update())
        // Ensure segment is initialized before play starts
        this.player().on('play', () => {
            const duration = this.player().duration() ?? 0
            // @ts-ignore
            if (!this.player().segment_ && duration > 0 && isFinite(duration)) {
                // @ts-ignore
                this.player().segment_ = { startTime: 0, endTime: duration }
            }
            this.update()
        })
    }

    createEl() {
        const el = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-control'],
        }) as HTMLElement

        this.mousedownHandler = (e: Event) => {
            e.stopPropagation()
        }
        this.touchstartHandler = (e: Event) => {
            e.stopPropagation()
        }
        el.addEventListener('mousedown', this.mousedownHandler, { capture: true })
        el.addEventListener('touchstart', this.touchstartHandler, { capture: true })

        this.trackEl = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-track'],
        }) as HTMLElement
        el.appendChild(this.trackEl)

        this.activeRangeEl = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-active'],
        }) as HTMLElement
        el.appendChild(this.activeRangeEl)

        const startHandleContainer = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-handle-container'],
        }) as HTMLElement
        startHandleContainer.setAttribute('data-handle', 'start')

        const startLabel = videojs.dom.createEl('span', {
            innerHTML: 'S',
            className: `${styles['vjs-segment-range-handle-label']} ${styles['vjs-segment-range-handle-label-start']}`,
        }) as HTMLElement

        this.startHandleEl = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-handle'],
        }) as HTMLElement

        startHandleContainer.appendChild(startLabel)
        startHandleContainer.appendChild(this.startHandleEl)
        this.startHandleContainer = startHandleContainer
        el.appendChild(startHandleContainer)

        const endHandleContainer = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-handle-container-end'],
        }) as HTMLElement
        endHandleContainer.setAttribute('data-handle', 'end')
        endHandleContainer.style.left = 'calc(100% - 15px)'

        this.endHandleEl = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-handle'],
        }) as HTMLElement

        const endLabel = videojs.dom.createEl('span', {
            innerHTML: 'E',
            className: `${styles['vjs-segment-range-handle-label']} ${styles['vjs-segment-range-handle-label-end']}`,
        }) as HTMLElement

        endHandleContainer.appendChild(this.endHandleEl)
        endHandleContainer.appendChild(endLabel)
        this.endHandleContainer = endHandleContainer
        el.appendChild(endHandleContainer)

        this.playheadEl = videojs.dom.createEl('div', {
            className: styles['vjs-segment-range-playhead'],
        }) as HTMLElement
        el.appendChild(this.playheadEl)

        requestAnimationFrame(() => {
            if (this.startHandleContainer && this.endHandleContainer && this.trackEl) {
                this.setupEventListeners()
                this.update()
            } else {
                const containerEl = this.el() as HTMLElement
                if (containerEl) {
                    const foundStartContainer = containerEl.querySelector(
                        '[data-handle="start"]'
                    ) as HTMLElement
                    const foundEndContainer = containerEl.querySelector(
                        '[data-handle="end"]'
                    ) as HTMLElement
                    const allDivs = containerEl.querySelectorAll('div')
                    let foundTrack: HTMLElement | null = null
                    for (const div of Array.from(allDivs)) {
                        if (div.classList.contains(styles['vjs-segment-range-track'])) {
                            foundTrack = div as HTMLElement
                            break
                        }
                    }

                    if (foundStartContainer) {
                        this.startHandleContainer = foundStartContainer
                        const handleDivs = foundStartContainer.querySelectorAll('div')
                        for (const div of Array.from(handleDivs)) {
                            if (div.classList.contains(styles['vjs-segment-range-handle'])) {
                                this.startHandleEl = div as HTMLElement
                                break
                            }
                        }
                    }
                    if (foundEndContainer) {
                        this.endHandleContainer = foundEndContainer
                        const handleDivs = foundEndContainer.querySelectorAll('div')
                        for (const div of Array.from(handleDivs)) {
                            if (div.classList.contains(styles['vjs-segment-range-handle'])) {
                                this.endHandleEl = div as HTMLElement
                                break
                            }
                        }
                    }
                    if (foundTrack) this.trackEl = foundTrack

                    if (this.startHandleContainer && this.endHandleContainer && this.trackEl) {
                        this.setupEventListeners()
                        this.update()
                    }
                }
            }
        })

        return el
    }

    setupEventListeners() {
        if (!this.startHandleContainer || !this.endHandleContainer || !this.trackEl) {
            return
        }

        if (this.listenersSetup) {
            return
        }

        this.listenersSetup = true

        const handleStartDrag = (handle: 'start' | 'end') => {
            return (e: Event) => {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()

                this.draggingHandle = handle

                this.updateHandlePosition(e, handle)

                if (this.boundMouseMove) {
                    document.addEventListener('mousemove', this.boundMouseMove, {
                        capture: true,
                        passive: false,
                    })
                    document.addEventListener('touchmove', this.boundMouseMove, {
                        capture: true,
                        passive: false,
                    })
                    document.addEventListener('pointermove', this.boundMouseMove, {
                        capture: true,
                        passive: false,
                    })
                }
                if (this.boundMouseUp) {
                    document.addEventListener('mouseup', this.boundMouseUp, {
                        capture: true,
                        passive: false,
                    })
                    document.addEventListener('touchend', this.boundMouseUp, {
                        capture: true,
                        passive: false,
                    })
                    document.addEventListener('pointerup', this.boundMouseUp, {
                        capture: true,
                        passive: false,
                    })
                }
            }
        }

        this.startDragStartHandler = handleStartDrag('start')
        this.startDragEndHandler = handleStartDrag('end')

        const events = ['mousedown', 'touchstart', 'pointerdown']
        events.forEach(eventType => {
            this.startHandleContainer!.addEventListener(
                eventType,
                this.startDragStartHandler as EventListener,
                { capture: true, passive: false }
            )
            this.endHandleContainer!.addEventListener(
                eventType,
                this.startDragEndHandler as EventListener,
                { capture: true, passive: false }
            )
            if (this.startHandleEl) {
                this.startHandleEl.addEventListener(
                    eventType,
                    this.startDragStartHandler as EventListener,
                    { capture: true, passive: false }
                )
            }
            if (this.endHandleEl) {
                this.endHandleEl.addEventListener(
                    eventType,
                    this.startDragEndHandler as EventListener,
                    { capture: true, passive: false }
                )
            }
        })

        this.trackClickHandler = (e: MouseEvent | TouchEvent) => {
            if (this.draggingHandle) {
                return
            }
            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()

            const mouseEvent = e as MouseEvent
            if (!('clientX' in mouseEvent)) {
                const touch =
                    (e as TouchEvent).touches?.[0] || (e as TouchEvent).changedTouches?.[0]
                if (touch) {
                    const syntheticEvent = new MouseEvent('click', {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        bubbles: true,
                        cancelable: true,
                    })
                    this.handleTrackClick(syntheticEvent)
                }
            } else {
                this.handleTrackClick(mouseEvent)
            }
        }

        this.trackEl.addEventListener('click', this.trackClickHandler as EventListener, {
            capture: true,
            passive: false,
        })
        this.trackEl.addEventListener('touchend', this.trackClickHandler as EventListener, {
            capture: true,
            passive: false,
        })
    }

    updateHandlePosition(e: Event, handle: 'start' | 'end') {
        if (!this.trackEl) {
            return
        }

        const mouseEvent = e as MouseEvent
        const touchEvent = e as TouchEvent
        const pointerEvent = e as PointerEvent

        let clientX = 0
        if (pointerEvent && 'clientX' in pointerEvent) {
            clientX = pointerEvent.clientX
        } else if (touchEvent.touches && touchEvent.touches[0]) {
            clientX = touchEvent.touches[0].clientX
        } else if (mouseEvent && 'clientX' in mouseEvent) {
            clientX = mouseEvent.clientX
        } else {
            return
        }

        const containerEl = this.el() as HTMLElement
        if (!containerEl) {
            return
        }

        const containerRect = containerEl.getBoundingClientRect()
        const trackPadding = 15
        const trackLeft = containerRect.left + trackPadding
        const trackWidth = containerRect.width - trackPadding * 2

        if (trackWidth <= 0) {
            return
        }

        const percent = Math.max(0, Math.min(1, (clientX - trackLeft) / trackWidth))
        const duration = this.player().duration() ?? 0

        if (duration === 0 || !duration || !isFinite(duration) || isNaN(duration)) {
            return
        }

        const newTime = percent * duration

        if (!isFinite(newTime) || isNaN(newTime)) {
            return
        }

        // @ts-ignore
        let segment = this.player().segment_ || null

        if (!segment) {
            const initialSegment = { startTime: 0, endTime: duration }
            // @ts-ignore
            this.player().segment_ = initialSegment
            segment = initialSegment
        }

        if (handle === 'start') {
            const newStartTime = Math.max(0, Math.min(newTime, segment.endTime - 0.1))
            if (isFinite(newStartTime) && !isNaN(newStartTime)) {
                this.updateSegment(newStartTime, segment.endTime)
            }
        } else {
            const newEndTime = Math.max(segment.startTime + 0.1, Math.min(newTime, duration))
            if (isFinite(newEndTime) && !isNaN(newEndTime)) {
                this.updateSegment(segment.startTime, newEndTime)
            }
        }
    }

    handleMouseMove = (e: MouseEvent | TouchEvent) => {
        if (!this.draggingHandle) {
            return
        }

        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()

        this.updateHandlePosition(e, this.draggingHandle)
        this.update()

        requestAnimationFrame(() => {
            this.update()
        })
    }

    handleTouchMove = (e: TouchEvent) => {
        this.handleMouseMove(e)
    }

    handleMouseUp = () => {
        if (!this.draggingHandle) return

        this.draggingHandle = null

        if (this.boundMouseMove) {
            document.removeEventListener('mousemove', this.boundMouseMove, true)
            document.removeEventListener('touchmove', this.boundMouseMove, true)
            document.removeEventListener('pointermove', this.boundMouseMove, true)
        }
        if (this.boundMouseUp) {
            document.removeEventListener('mouseup', this.boundMouseUp, true)
            document.removeEventListener('touchend', this.boundMouseUp, true)
            document.removeEventListener('pointerup', this.boundMouseUp, true)
        }
    }

    dispose() {
        const el = this.el() as HTMLElement

        this.player().off('timeupdate')
        this.player().off('segmentchange')
        this.player().off('loadedmetadata')
        this.player().off('play')

        if (this.boundMouseMove) {
            document.removeEventListener('mousemove', this.boundMouseMove, true)
            document.removeEventListener('touchmove', this.boundMouseMove, true)
            document.removeEventListener('pointermove', this.boundMouseMove, true)
        }
        if (this.boundMouseUp) {
            document.removeEventListener('mouseup', this.boundMouseUp, true)
            document.removeEventListener('touchend', this.boundMouseUp, true)
            document.removeEventListener('pointerup', this.boundMouseUp, true)
        }

        if (el && this.mousedownHandler) {
            el.removeEventListener('mousedown', this.mousedownHandler, true)
        }
        if (el && this.touchstartHandler) {
            el.removeEventListener('touchstart', this.touchstartHandler, true)
        }

        const events = ['mousedown', 'touchstart', 'pointerdown']
        events.forEach(eventType => {
            if (this.startHandleContainer && this.startDragStartHandler) {
                this.startHandleContainer.removeEventListener(
                    eventType,
                    this.startDragStartHandler as EventListener,
                    true
                )
            }
            if (this.endHandleContainer && this.startDragEndHandler) {
                this.endHandleContainer.removeEventListener(
                    eventType,
                    this.startDragEndHandler as EventListener,
                    true
                )
            }
            if (this.startHandleEl && this.startDragStartHandler) {
                this.startHandleEl.removeEventListener(
                    eventType,
                    this.startDragStartHandler as EventListener,
                    true
                )
            }
            if (this.endHandleEl && this.startDragEndHandler) {
                this.endHandleEl.removeEventListener(
                    eventType,
                    this.startDragEndHandler as EventListener,
                    true
                )
            }
        })

        if (this.trackEl && this.trackClickHandler) {
            this.trackEl.removeEventListener('click', this.trackClickHandler as EventListener, true)
            this.trackEl.removeEventListener(
                'touchend',
                this.trackClickHandler as EventListener,
                true
            )
        }

        this.startHandleContainer = null
        this.endHandleContainer = null
        this.startHandleEl = null
        this.endHandleEl = null
        this.trackEl = null
        this.activeRangeEl = null
        this.playheadEl = null
        this.boundMouseMove = null
        this.boundMouseUp = null
        this.draggingHandle = null
        this.listenersSetup = false
        this._initializingSegment = false
        this._segmentInitialized = false
        this.startDragStartHandler = null
        this.startDragEndHandler = null
        this.trackClickHandler = null
        this.mousedownHandler = null
        this.touchstartHandler = null

        super.dispose()
    }

    handleTrackClick = (e: MouseEvent) => {
        if (!this.trackEl) return

        const player = this.player()

        const readyState = player.readyState()
        if (readyState === 0) {
            return
        }

        const rect = this.trackEl.getBoundingClientRect()
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        const duration = player.duration() ?? 0

        if (!duration || duration === 0 || !isFinite(duration) || isNaN(duration)) {
            return
        }

        let newTime = percent * duration

        if (!isFinite(newTime) || isNaN(newTime)) {
            return
        }

        // @ts-ignore
        const segment = player.segment_ || null
        if (segment) {
            newTime = Math.max(segment.startTime, Math.min(newTime, segment.endTime))
        }

        if (isFinite(newTime) && !isNaN(newTime) && newTime >= 0) {
            try {
                player.currentTime(newTime)
            } catch (error) {
                // Error setting currentTime
            }
        }
    }

    updateSegment(startTime: number, endTime: number) {
        if (!isFinite(startTime) || !isFinite(endTime) || isNaN(startTime) || isNaN(endTime)) {
            return
        }

        const player = this.player()
        const duration = player.duration() ?? 0

        if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
            return
        }

        const validStartTime = Math.max(0, Math.min(startTime, duration))
        const validEndTime = Math.max(validStartTime, Math.min(endTime, duration))

        if (
            !isFinite(validStartTime) ||
            !isFinite(validEndTime) ||
            isNaN(validStartTime) ||
            isNaN(validEndTime) ||
            validStartTime < 0 ||
            validEndTime < validStartTime
        ) {
            return
        }

        // @ts-ignore
        const oldSegment = player.segment_ || null

        this._segmentInitialized = true

        const newSegment = { startTime: validStartTime, endTime: validEndTime }
        const hasChanged =
            !oldSegment ||
            oldSegment.startTime !== validStartTime ||
            oldSegment.endTime !== validEndTime

        // @ts-ignore
        player.segment_ = newSegment

        // Trigger segmentchange event if segment actually changed
        if (hasChanged) {
            player.trigger('segmentchange')
        }

        const eventData = { startTime: validStartTime, endTime: validEndTime }
        player.trigger('segmentupdate', eventData)

        this.update()
        requestAnimationFrame(() => {
            this.update()
        })
    }

    update() {
        if (
            !this.startHandleContainer ||
            !this.endHandleContainer ||
            !this.activeRangeEl ||
            !this.trackEl ||
            !this.playheadEl
        ) {
            const containerEl = this.el() as HTMLElement
            if (containerEl) {
                const foundStartContainer = containerEl.querySelector(
                    '[data-handle="start"]'
                ) as HTMLElement
                const foundEndContainer = containerEl.querySelector(
                    '[data-handle="end"]'
                ) as HTMLElement

                const allDivs = containerEl.querySelectorAll('div')
                let foundTrack: HTMLElement | null = null
                let foundActive: HTMLElement | null = null
                let foundPlayhead: HTMLElement | null = null

                for (const div of Array.from(allDivs)) {
                    if (div.classList.contains(styles['vjs-segment-range-track'])) {
                        foundTrack = div as HTMLElement
                    }
                    if (div.classList.contains(styles['vjs-segment-range-active'])) {
                        foundActive = div as HTMLElement
                    }
                    if (div.classList.contains(styles['vjs-segment-range-playhead'])) {
                        foundPlayhead = div as HTMLElement
                    }
                }

                if (foundStartContainer) {
                    this.startHandleContainer = foundStartContainer
                    const handleDivs = foundStartContainer.querySelectorAll('div')
                    for (const div of Array.from(handleDivs)) {
                        if (div.classList.contains(styles['vjs-segment-range-handle'])) {
                            this.startHandleEl = div as HTMLElement
                            break
                        }
                    }
                }
                if (foundEndContainer) {
                    this.endHandleContainer = foundEndContainer
                    const handleDivs = foundEndContainer.querySelectorAll('div')
                    for (const div of Array.from(handleDivs)) {
                        if (div.classList.contains(styles['vjs-segment-range-handle'])) {
                            this.endHandleEl = div as HTMLElement
                            break
                        }
                    }
                }
                if (foundTrack) this.trackEl = foundTrack
                if (foundActive) this.activeRangeEl = foundActive
                if (foundPlayhead) this.playheadEl = foundPlayhead
            }
        }

        if (
            !this.startHandleContainer ||
            !this.endHandleContainer ||
            !this.activeRangeEl ||
            !this.trackEl ||
            !this.playheadEl
        ) {
            return
        }

        const duration = this.player()?.duration() ?? 0

        if (!duration || duration === 0 || !isFinite(duration) || isNaN(duration)) {
            return
        }

        const containerEl = this.el() as HTMLElement
        if (!containerEl) {
            return
        }

        const containerRect = containerEl.getBoundingClientRect()
        const containerWidth = containerRect.width || containerEl.offsetWidth || 200
        const trackPadding = 15
        const trackWidth = containerWidth - trackPadding * 2

        if (trackWidth <= 0) {
            return
        }

        // @ts-ignore
        let segment = this.player().segment_ || null

        if (
            !segment &&
            duration > 0 &&
            isFinite(duration) &&
            !this.draggingHandle &&
            !this._initializingSegment &&
            !this._segmentInitialized
        ) {
            this._initializingSegment = true
            this._segmentInitialized = true
            segment = { startTime: 0, endTime: duration }
            // @ts-ignore
            this.player().segment_ = segment
            setTimeout(() => {
                this._initializingSegment = false
            }, 100)
        } else if (segment) {
            this._segmentInitialized = true
            this._initializingSegment = false
        }

        if (segment && segment.startTime !== undefined && segment.endTime !== undefined) {
            if (
                !isFinite(segment.startTime) ||
                !isFinite(segment.endTime) ||
                isNaN(segment.startTime) ||
                isNaN(segment.endTime)
            ) {
                return
            }
            const { startTime, endTime } = segment
            const startPercent = Math.max(0, Math.min(100, (startTime / duration) * 100))
            const endPercent = Math.max(startPercent, Math.min(100, (endTime / duration) * 100))

            const clampedStartPercent = Math.max(0, Math.min(100, startPercent))
            const clampedEndPercent = Math.max(clampedStartPercent, Math.min(100, endPercent))

            const startLeftPx = trackPadding + (clampedStartPercent / 100) * trackWidth
            const endLeftPx = trackPadding + (clampedEndPercent / 100) * trackWidth

            this.startHandleContainer.style.left = `${startLeftPx}px`
            this.endHandleContainer.style.left = `${endLeftPx}px`

            const activeLeftPx = trackPadding + (clampedStartPercent / 100) * trackWidth
            const activeWidthPx = ((clampedEndPercent - clampedStartPercent) / 100) * trackWidth

            this.activeRangeEl.style.left = `${activeLeftPx}px`
            this.activeRangeEl.style.width = `${activeWidthPx}px`

            const currentTime = this.player().currentTime() ?? 0
            const clampedCurrentTime = Math.max(startTime, Math.min(currentTime, endTime))
            const currentPercent = (clampedCurrentTime / duration) * 100
            const clampedCurrentPercent = Math.max(0, Math.min(100, currentPercent))

            const playheadLeftPx = trackPadding + (clampedCurrentPercent / 100) * trackWidth

            this.playheadEl.style.left = `${playheadLeftPx}px`
            this.playheadEl.style.display = 'block'
            this.playheadEl.style.opacity = '1'
            this.playheadEl.style.visibility = 'visible'
        } else {
            // @ts-ignore
            const segmentPlugin = this.player().segment()
            if (!segmentPlugin.hasSegment() && duration > 0 && isFinite(duration)) {
                segmentPlugin.setSegment({ startTime: 0, endTime: duration }, true)
                requestAnimationFrame(() => {
                    this.update()
                })
                return
            }

            this.startHandleContainer.style.left = `${trackPadding}px`
            this.endHandleContainer.style.left = `${trackPadding + trackWidth}px`

            this.activeRangeEl.style.left = `${trackPadding}px`
            this.activeRangeEl.style.width = `${trackWidth}px`

            const currentTime = this.player().currentTime() ?? 0
            if (isFinite(currentTime) && !isNaN(currentTime) && duration > 0) {
                const currentPercent = (currentTime / duration) * 100
                const clampedCurrentPercent = Math.max(0, Math.min(100, currentPercent))

                const playheadLeftPx = trackPadding + (clampedCurrentPercent / 100) * trackWidth

                this.playheadEl.style.left = `${playheadLeftPx}px`
                this.playheadEl.style.display = 'block'
                this.playheadEl.style.opacity = '1'
                this.playheadEl.style.visibility = 'visible'
            }
        }
    }
}
